import React, { useState, useEffect, useRef } from 'react';
import './processOrder.css';

const ProcessOrder = ({ inquiryData, invoiceData, onBack, onOrderCreated }) => {
  // Derive initial shipping type and container type from inquiryData.shippingType
  const deriveShippingDefaults = () => {
    const raw = (inquiryData?.shippingType || '').toLowerCase().trim();
    if (raw.includes('20ft') || raw.includes('20 ft')) {
      return { type: 'container', container: '20ft' };
    }
    if (raw.includes('40ft') || raw.includes('40 ft')) {
      return { type: 'container', container: '40ft' };
    }
    if (raw.includes('individual')) {
      return { type: 'individual', container: '' };
    }
    return { type: '', container: '' };
  };

  const shippingDefaults = deriveShippingDefaults();

  const [shippingType, setShippingType] = useState(shippingDefaults.type);
  const [containerType, setContainerType] = useState(shippingDefaults.container);
  const [shippingOption, setShippingOption] = useState(inquiryData?.shippingOption || '');
  const [shippingCost, setShippingCost] = useState(inquiryData?.baseShippingCost || '');
  const [totalCost, setTotalCost] = useState(inquiryData?.totalCost || '');
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const uploadAreaRef = useRef(null);

  useEffect(() => {
    if (!uploadAreaRef.current) return;
    if (!paymentReceipt) {
      uploadAreaRef.current.style.setProperty('border-color', '#dc2626', 'important');
    } else {
      uploadAreaRef.current.style.removeProperty('border-color');
    }
  }, [paymentReceipt]);
  const [paymentReceiptPreview, setPaymentReceiptPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [orderResponse, setOrderResponse] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const invoiceNo = inquiryData?.proformaInvoiceNo || invoiceData?.proformaInvoiceNo || '';
  const id = invoiceData?.proformaInvoiceId || invoiceData?.id || inquiryData?.proformaInvoiceId || inquiryData?.id || '';
  const grandTotal = invoiceData?.grandTotal || inquiryData?.totalCost || '';
  
  useEffect(() => {
    if (invoiceData) {
      setShippingCost(invoiceData.shippingCost || inquiryData?.baseShippingCost || '');
      setTotalCost(invoiceData.grandTotal || inquiryData?.totalCost || '');
      setShippingOption(invoiceData.shippingOption || inquiryData?.shippingOption || '');
    }
    calculateTotalCost(shippingCost);
  }, [invoiceData, inquiryData, shippingCost]);

  const calculateTotalCost = (shipping) => {
    const carPrice = parseFloat(inquiryData?.carPrice || 0);
    const shippingAmount = parseFloat(shipping || 0);
    const total = carPrice + shippingAmount;
    setTotalCost(total.toFixed(2));
    return total.toFixed(2);
  };

  const handleShippingTypeChange = (e) => {
    const value = e.target.value;
    setShippingType(value);
    setContainerType('');
    setFormErrors(prev => ({ ...prev, shippingType: '' }));
  };

  const handleContainerTypeChange = (value) => {
    setContainerType(value);
    setFormErrors(prev => ({ ...prev, containerType: '' }));
  };

  const handleShippingOptionChange = (e) => {
    setShippingOption(e.target.value);
    setFormErrors(prev => ({ ...prev, shippingOption: '' }));
  };

  const handleShippingCostChange = (e) => {
    const value = e.target.value;
    setShippingCost(value);
    calculateTotalCost(value);
    setFormErrors(prev => ({ ...prev, shippingCost: '' }));
  };

  const handleTotalCostChange = (e) => {
    setTotalCost(e.target.value);
  };

  const handlePaymentReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        alert('Only JPG, PNG, and PDF files are allowed');
        return;
      }
      setPaymentReceipt(file);
      setFormErrors(prev => ({ ...prev, paymentReceipt: '' }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const errors = {};
    const missing = [];
    if (!shippingType) { errors.shippingType = 'Required'; missing.push('Shipping Type'); }
    if (shippingType === 'container' && !containerType) { errors.containerType = 'Required'; missing.push('Container Type'); }
    if (!shippingOption) { errors.shippingOption = 'Required'; missing.push('Shipping Option'); }
    if (shippingCost === '' || shippingCost === null || shippingCost === undefined || isNaN(parseFloat(shippingCost)) || parseFloat(shippingCost) < 0) { errors.shippingCost = 'Required'; missing.push('Shipping Cost'); }
    if (!totalCost || parseFloat(totalCost) <= 0) { errors.totalCost = 'Required'; missing.push('Total Cost'); }
    if (!paymentReceipt) { errors.paymentReceipt = 'Payment receipt is required'; missing.push('Payment Receipt'); }
    setFormErrors(errors);
    return { isValid: Object.keys(errors).length === 0, missing };
  };

  const handleConfirm = async () => {
    const { isValid, missing } = validateForm();
    if (!isValid) {
      setMissingFields(missing);
      setShowErrorDialog(true);
      return;
    }
    setIsSubmitting(true);
    const apiPayload = {
      emailId: inquiryData?.userEmailId || invoiceData?.email || '',
      totalBaseShippingCostAll: parseFloat(shippingCost) || 0,
      totalCostAllCars: parseFloat(inquiryData?.carPrice || invoiceData?.carPrice || 0),
      totalCost: parseFloat(totalCost) || 0,
      carModelId: inquiryData?.carModelId || invoiceData?.carModelId || 0,
      countryName: inquiryData?.countryName || invoiceData?.countryName || '',
      portName: inquiryData?.portName || invoiceData?.portName || '',
      volumeCubicMeters: parseFloat(inquiryData?.volumeCubicMeters || invoiceData?.m3 || 0),
      shippingOption: shippingOption,
      proformaInvoiceNo: invoiceNo || '',
      id: id || undefined
    };
    console.log('Sending API payload:', apiPayload);
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/calculate-order-history/save/new', {
        method: 'POST',
        headers: { 'accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(apiPayload)
      });
      console.log('Response status:', response.status);
      const contentType = response.headers.get('content-type');
      let data = null;
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            throw new Error('Invalid JSON response from server');
          }
        }
      }
      if (response.status === 201 || response.ok) {
        if (!data) {
          data = {
            message: 'Order created successfully',
            orderRefId: `ORD-${Date.now()}`,
            dateOfOrder: new Date().toLocaleDateString('en-GB'),
            totalCost: totalCost,
            totalBaseShippingCostAll: shippingCost,
            totalCostAllCars: inquiryData?.carPrice || invoiceData?.carPrice || 0,
            proformaInvoiceNo: invoiceNo,
            id: id
          };
        }
        if (!data.proformaInvoiceNo && invoiceNo) data.proformaInvoiceNo = invoiceNo;
        if (!data.id && id) data.id = id;
        setOrderResponse(data);
        setShowSuccessDialog(true);
      } else {
        const errorMessage = data?.message || data?.error || `Server error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      alert(`Failed to create order: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    if (onOrderCreated) {
      onOrderCreated(orderResponse);
    } else if (onBack) {
      onBack();
    }
  };

  const handlePrintOrder = () => {
    const printContent = `
      <html>
        <head>
          <title>Order Summary - ${orderResponse?.orderRefId || 'N/A'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .section { margin-bottom: 20px; }
            .section-title { background: #f5f5f5; padding: 10px; font-weight: bold; }
            .row { display: flex; justify-content: space-between; padding: 5px 0; }
            .label { font-weight: bold; }
            .value { text-align: right; }
            .total-row { border-top: 2px solid #000; margin-top: 10px; padding-top: 10px; font-weight: bold; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
            @media print { body { padding: 0; } .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Order Confirmation</h1>
            <p>Order Reference: ${orderResponse?.orderRefId || 'N/A'}</p>
            <p>Date: ${orderResponse?.dateOfOrder || new Date().toLocaleDateString()}</p>
          </div>
          <div class="section">
            <div class="section-title">Customer Information</div>
            <div class="row"><span class="label">Name:</span><span class="value">${inquiryData?.firstName || ''} ${inquiryData?.lastName || ''}</span></div>
            <div class="row"><span class="label">Email:</span><span class="value">${inquiryData?.userEmailId || invoiceData?.email || 'N/A'}</span></div>
            <div class="row"><span class="label">Country Code:</span><span class="value">${inquiryData?.guestCountryCode || 'N/A'}</span></div>
            <div class="row"><span class="label">Phone:</span><span class="value">${inquiryData?.userMobileNumber || invoiceData?.phoneNumber || 'N/A'}</span></div>
          </div>
          <div class="section">
            <div class="section-title">Vehicle Information</div>
            <div class="row"><span class="label">Vehicle:</span><span class="value">${inquiryData?.carBrandName || ''} ${inquiryData?.carModelName || ''}</span></div>
            <div class="row"><span class="label">Reference No:</span><span class="value">${inquiryData?.carRefNumber || 'N/A'}</span></div>
            <div class="row"><span class="label">Engine CC:</span><span class="value">${inquiryData?.carEngineCC || 'N/A'}</span></div>
          </div>
          <div class="section">
            <div class="section-title">Shipping Information</div>
            <div class="row"><span class="label">Shipping Type:</span><span class="value">${shippingType}</span></div>
            ${shippingType === 'container' ? `<div class="row"><span class="label">Container Type:</span><span class="value">${containerType}</span></div>` : ''}
            <div class="row"><span class="label">Shipping Option:</span><span class="value">${shippingOption}</span></div>
            <div class="row"><span class="label">Destination:</span><span class="value">${inquiryData?.countryName || ''} (${inquiryData?.portName || ''})</span></div>
          </div>
          <div class="section">
            <div class="section-title">Invoice Information</div>
            ${invoiceNo ? `<div class="row"><span class="label">Proforma Invoice No:</span><span class="value">${invoiceNo}</span></div>` : ''}
          </div>
          <div class="section">
            <div class="section-title">Cost Breakdown</div>
            <div class="row"><span class="label">Vehicle Cost:</span><span class="value">$${inquiryData?.carPrice || 0}</span></div>
            <div class="row"><span class="label">Shipping Cost:</span><span class="value">$${shippingCost}</span></div>
            <div class="row total-row"><span class="label">Total Cost:</span><span class="value">$${totalCost}</span></div>
          </div>
          <div class="footer">
            <p>Order generated on ${new Date().toLocaleString()}</p>
            <p>AMAR ENTERPRISES CO LTD</p>
          </div>
          <div class="no-print" style="margin-top: 20px; text-align: center;">
            <button onclick="window.print()">Print Order</button>
            <button onclick="window.close()">Close</button>
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      if (onBack) onBack();
    }
  };

  return (
    <div className="process-order-container">
      {/* Missing Fields Error Dialog */}
      {showErrorDialog && (
        <div
          onClick={() => setShowErrorDialog(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: '8px', width: '90%',
              maxWidth: '420px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', overflow: 'hidden',
            }}
          >
            <div style={{
              backgroundColor: '#fdecea', padding: '16px 20px', display: 'flex',
              alignItems: 'center', gap: '10px', borderBottom: '1px solid #f5c6c8',
            }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <h3 style={{ margin: 0, color: '#b02a2a', fontSize: '1.1rem' }}>Error</h3>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ margin: '0 0 10px 0', color: '#333' }}>Please fill in the following required fields:</p>
              <p style={{ margin: 0, color: '#dc2626', fontWeight: 600 }}>{missingFields.join(', ')}</p>
            </div>
            <div style={{ padding: '0 20px 20px 20px', textAlign: 'right' }}>
              <button
                onClick={() => setShowErrorDialog(false)}
                style={{
                  backgroundColor: '#2563eb', color: 'white', border: 'none',
                  borderRadius: '4px', padding: '8px 24px', fontSize: '0.95rem',
                  cursor: 'pointer', fontWeight: 500,
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      {showSuccessDialog && orderResponse && (
        <div className="success-dialog-overlay" onClick={handleSuccessDialogClose}>
          <div className="success-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="success-dialog-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#10b981"/>
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Order Created Successfully!</h2>
            <div className="success-dialog-content">
              <p className="success-message">{orderResponse?.message || 'Order has been created successfully.'}</p>
              <div className="order-details">
                <div className="order-detail-item">
                  <span className="detail-label">Order Reference ID:</span>
                  <span className="detail-value highlight">{orderResponse?.orderRefId || 'N/A'}</span>
                </div>
                <div className="order-detail-item">
                  <span className="detail-label">Order Date:</span>
                  <span className="detail-value">{orderResponse?.dateOfOrder || new Date().toLocaleDateString()}</span>
                </div>
                <div className="order-detail-item">
                  <span className="detail-label">Total Cost:</span>
                  <span className="detail-value price">${orderResponse?.totalCost || totalCost}</span>
                </div>
                <div className="order-detail-item">
                  <span className="detail-label">Shipping Cost:</span>
                  <span className="detail-value">${orderResponse?.totalBaseShippingCostAll || shippingCost}</span>
                </div>
                <div className="order-detail-item">
                  <span className="detail-label">Car Cost:</span>
                  <span className="detail-value">${orderResponse?.totalCostAllCars || inquiryData?.carPrice || 0}</span>
                </div>
                {(orderResponse?.proformaInvoiceNo || invoiceNo) && (
                  <div className="order-detail-item">
                    <span className="detail-label">Proforma Invoice No:</span>
                    <span className="detail-value highlight">{orderResponse?.proformaInvoiceNo || invoiceNo}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="success-dialog-actions">
              <button className="success-dialog-btn secondary" onClick={handlePrintOrder}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                </svg>
                Print Order
              </button>
              <button className="success-dialog-btn primary" onClick={handleSuccessDialogClose}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                Close & Return
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="process-order-header">
        <button className="back-button" onClick={handleCancel}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"/>
          </svg>
          Back to Inquiries
        </button>
        <div>
          <h1>Process Order</h1>
        </div>
        <div className="order-status-badge">
          <span className="status-dot"></span>
          Processing Order
        </div>
      </div>

      <div className="process-order-content">
        {/* Invoice Information Section (if available) */}
        {invoiceNo && (
          <div className="order-section invoice-info-section">
            <div className="section-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
              </svg>
              <h2>Invoice Information</h2>
            </div>
            <div className="info-grid">
              <div className="info-item">
                <label>Proforma Invoice Number</label>
                <div className="info-value highlight">{invoiceNo}</div>
              </div>
              {grandTotal && (
                <div className="info-item">
                  <label>Invoice Amount</label>
                  <div className="info-value price">${grandTotal}</div>
                </div>
              )}
              {invoiceData?.invoiceDate && (
                <div className="info-item">
                  <label>Invoice Date</label>
                  <div className="info-value">{invoiceData.invoiceDate}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Customer Information Section */}
        <div className="order-section customer-info-section">
          <div className="section-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            <h2>Customer Information</h2>
          </div>
          <div className="info-grid">
            <div className="info-item">
              <label>First Name</label>
              <div className="info-value">{inquiryData?.firstName || invoiceData?.customerName?.split(' ')[0] || 'N/A'}</div>
            </div>
            <div className="info-item">
              <label>Last Name</label>
              <div className="info-value">{inquiryData?.lastName || invoiceData?.customerName?.split(' ').slice(1).join(' ') || 'N/A'}</div>
            </div>
            <div className="info-item">
              <label>Email Address</label>
              <div className="info-value">{inquiryData?.userEmailId || invoiceData?.email || 'N/A'}</div>
            </div>
            <div className="info-item">
              <label>Country Code</label>
              <div className="info-value">{inquiryData?.guestCountryCode || 'N/A'}</div>
            </div>
            <div className="info-item">
              <label>Phone Number</label>
              <div className="info-value">{inquiryData?.userMobileNumber || invoiceData?.phoneNumber || 'N/A'}</div>
            </div>
            <div className="info-item">
              <label>Country</label>
              <div className="info-value">{inquiryData?.countryName || invoiceData?.countryName || 'N/A'}</div>
            </div>
            <div className="info-item">
              <label>Port</label>
              <div className="info-value">{inquiryData?.portName || invoiceData?.portName || 'N/A'}</div>
            </div>
          </div>
        </div>

        {/* Vehicle Information Section */}
        <div className="order-section vehicle-info-section">
          <div className="section-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
            </svg>
            <h2>Vehicle Information</h2>
          </div>
          <div className="info-grid">
            <div className="info-item">
              <label>Car Name</label>
              <div className="info-value highlight">{inquiryData?.carBrandName} {inquiryData?.carModelName}</div>
            </div>
            <div className="info-item">
              <label>Car Price</label>
              <div className="info-value price">${inquiryData?.carPrice || 'N/A'}</div>
            </div>
            <div className="info-item">
              <label>Reference Number</label>
              <div className="info-value">{inquiryData?.carRefNumber || 'N/A'}</div>
            </div>
            <div className="info-item">
              <label>Engine CC</label>
              <div className="info-value">{inquiryData?.carEngineCC || 'N/A'}</div>
            </div>
            <div className="info-item">
              <label>Color</label>
              <div className="info-value">{inquiryData?.carColor || 'N/A'}</div>
            </div>
          </div>
        </div>

        {/* Shipping & Cost Information Section */}
        <div className="order-section shipping-cost-section">
          <div className="section-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
            </svg>
            <h2>Shipping & Cost Details</h2>
            <span className="editable-badge">Editable</span>
          </div>
          <div className="editable-grid">
            <div className="editable-item">
              <label htmlFor="shippingType">Shipping Type *</label>
              <select
                id="shippingType"
                value={shippingType}
                onChange={handleShippingTypeChange}
                className="editable-input"
                style={(formErrors.shippingType || !shippingType) ? { borderColor: '#dc2626' } : {}}
              >
                <option value="">Select Shipping Type</option>
                <option value="container">Container</option>
                <option value="individual">Individual</option>
              </select>
              {formErrors.shippingType && (
                <span className="error-message">{formErrors.shippingType}</span>
              )}
            </div>

            {shippingType === 'container' && (
              <div className="editable-item">
                <label>Container Type *</label>
                <div className="container-type-options">
                  <label className={`container-type-option ${containerType === '20ft' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="containerType"
                      value="20ft"
                      checked={containerType === '20ft'}
                      onChange={(e) => handleContainerTypeChange(e.target.value)}
                    />
                    20ft Container
                  </label>
                  <label className={`container-type-option ${containerType === '40ft' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="containerType"
                      value="40ft"
                      checked={containerType === '40ft'}
                      onChange={(e) => handleContainerTypeChange(e.target.value)}
                    />
                    40ft Container
                  </label>
                </div>
                {formErrors.containerType && (
                  <span className="error-message">{formErrors.containerType}</span>
                )}
              </div>
            )}

            <div className="editable-item">
              <label htmlFor="shippingOption">Shipping Option *</label>
              <select
                id="shippingOption"
                value={shippingOption}
                onChange={handleShippingOptionChange}
                className="editable-input"
                style={(formErrors.shippingOption || !shippingOption) ? { borderColor: '#dc2626' } : {}}
              >
                <option value="">Select Shipping Option Type</option>
                <option value="CIF">CIF</option>
                <option value="CNF">C&F</option>
                <option value="FOB">FOB</option>
              </select>
              {formErrors.shippingOption && (
                <span className="error-message">{formErrors.shippingOption}</span>
              )}
            </div>
            <div className="editable-item">
              <label htmlFor="shippingCost">Shipping Cost ($) *</label>
              <input
                type="number"
                id="shippingCost"
                value={shippingCost}
                onChange={handleShippingCostChange}
                placeholder="Enter shipping cost"
                className="editable-input"
                style={(formErrors.shippingCost || shippingCost === '' || shippingCost === null || shippingCost === undefined) ? { borderColor: '#dc2626' } : {}}
                min="0"
                step="0.01"
              />
              {formErrors.shippingCost && (
                <span className="error-message">{formErrors.shippingCost}</span>
              )}
            </div>
            <div className="editable-item total-cost-item">
              <label htmlFor="totalCost">Total Cost ($) *</label>
              <input
                type="number"
                id="totalCost"
                value={totalCost}
                onChange={handleTotalCostChange}
                placeholder="Total cost"
                className={`editable-input total-cost-input ${formErrors.totalCost ? 'error' : ''}`}
                min="0"
                step="0.01"
              />
              {formErrors.totalCost && (
                <span className="error-message">{formErrors.totalCost}</span>
              )}
              <div className="cost-breakdown">
                <span>Car Price: ${inquiryData?.carPrice || 0}</span>
                <span>+ Shipping: ${shippingCost || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Receipt Upload Section */}
        <div className="order-section payment-section">
          <div className="section-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
            </svg>
            <h2>Payment Receipt</h2>
            <span className="required-badge" style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #dc2626', borderRadius: '12px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: 600 }}>Required</span>
          </div>
          <div className="payment-upload-container">
            <div
              ref={uploadAreaRef}
              className="upload-area"
            >
              <input
                type="file"
                id="paymentReceipt"
                accept="image/*,.pdf"
                onChange={handlePaymentReceiptUpload}
                className="file-input"
              />
              <label htmlFor="paymentReceipt" className="upload-label">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                </svg>
                <span className="upload-text">
                  {paymentReceipt ? paymentReceipt.name : 'Click to upload payment receipt'}
                </span>
                <span className="upload-subtext">PDF, PNG, JPG up to 10MB</span>
              </label>
            </div>
            {formErrors.paymentReceipt && (
              <span className="error-message">{formErrors.paymentReceipt}</span>
            )}
            {paymentReceiptPreview && (
              <div className="receipt-preview">
                <h4>Preview:</h4>
                {paymentReceipt?.type?.includes('pdf') ? (
                  <div className="pdf-preview">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="#dc2626">
                      <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                    </svg>
                    <p>{paymentReceipt.name}</p>
                  </div>
                ) : (
                  <img src={paymentReceiptPreview} alt="Payment receipt preview" />
                )}
                <button
                  className="remove-receipt-btn"
                  onClick={() => {
                    setPaymentReceipt(null);
                    setPaymentReceiptPreview('');
                  }}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="order-actions">
          <button
            className="cancel-order-btn"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
            Cancel
          </button>
          <button
            className="confirm-order-btn"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="spinner"></div>
                Processing...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                Confirm Order
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProcessOrder;